import { useState, useEffect, useContext } from "react";
import { BrandContext } from "../../../App";
import PageTitle from "../../PageTitle/PageTitle";
import BorderCard from "../../BorderCard/BorderCard";
import { Tabs, Tab, Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LinearProgress from "@mui/material/LinearProgress";

import { useSwipeable } from "react-swipeable";

import Personal from "./Forms/Personal";
import Auth from "./Forms/Auth";
import Notifications from "./Forms/Notifications";
import Billing from "./Forms/Billing";

import { ProfileContainer } from "./style";

import { useUI } from "../../../contexts/UIContext";
import { useAuth } from "../../../contexts/AuthContext";

const Profile = () => {
  const theme = useTheme();
  const [value, setValue] = useState(0);
  const { brand, partialAuthToken } = useContext(BrandContext);

  const { setSideMenuOpen } = useUI();
  const { fetchUser, authToken, loggedInUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  // Clear state when switching tabs (replaces useEffect)
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setError(false);
    setSuccess(false);
    setLoading(false);
  }

  useEffect(() => {
    setSideMenuOpen(false);
    return () => {
      setSideMenuOpen(true);
    };
  }, []);

  let tabContent = [
    {
      title: "Personal Info",
      subTitle: "Update your personal details here",
      component: Personal,
    },
    {
      title: "Password",
      header: "Change password",
      subTitle: "Update your password using your current password.",
      component: Auth,
    },
    {
      title: "Billing",
      subTitle: "Manage payment preferences",
      component: Billing,
    },
    {
      title: "Notifications",
      subTitle: "Choose when and how we contact you",
      component: Notifications,
    },
  ];

  const TabComponent = tabContent[value].component as React.Node;

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (value < 3) {
        // Allow swiping to all tabs
        setValue(value + 1);
      }
    },
    onSwipedRight: () => {
      if (value > 0) {
        // Allow swiping to all tabs
        setValue(value - 1);
      }
    },
    trackMouse: false, // Enable for desktop testing (optional)
    preventDefaultTouchmoveEvent: true, // Prevent page scrolling on swipe
    delta: 10, // Sensitivity for swipe detection
  });

  return (
    <ProfileContainer id="user-profile">
      <PageTitle topText="Account" text="Settings">Manage your personal details, authentication, billing and notifcation preferences.</PageTitle>
      <div className="swipeable-container" {...handlers}>
        <Tabs
          value={value}
          onChange={(event, newValue) => setValue(newValue)}
          TabIndicatorProps={{ style: { display: "none" } }}
          textColor={theme.palette.text.primary}
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",

            padding: 0,
            mb: theme.customSpacing["sm"],
            gap: "20px",
            backgroundColor: "transparent",
            "& .MuiTabs-list": {
              [theme.breakpoints.up("md")]: {
                gap: "15px",
              },
              "& button": {
                minWidth: 0,
                flex: 1,
                opacity: ".6",
              },
              "& button:disabled": {
                opacity: ".4",
              },
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: theme.typography.fontWeightMedium,
              color: theme.palette.text.primary,
              padding: `${theme.customSpacing.sm} ${theme.customSpacing.lg}`,
              borderRadius: "4px",
              minHeight: "unset",
              minWidth: "unset",
              gap: "20px",
            },
            "& .Mui-selected": {
              backgroundColor: theme.palette.foreground.button_secondary,
              color: theme.palette.background.brand_alt,
              fontWeight: theme.typography.fontWeightRegular,
              opacity: "1!important",
              "& button": {
                opacity: 1,
              },
            },
          }}
        >
          {tabContent.map((tab, index) => {
            return (
              <Tab
                key={index}
                disabled={tab.title === "Billing"}
                label={tab.title}
              />
            );
          })}
        </Tabs>

        <BorderCard
          title={tabContent[value].header || tabContent[value].title}
          subTitle={tabContent[value].subTitle}
        >
          {loggedInUser && Object.keys(loggedInUser).length > 0 ? (
            <>
              <TabComponent
                user={loggedInUser}
                setLoading={setLoading}
                setError={setError}
                setSuccess={setSuccess}
                brand={brand}
                authToken={authToken}
                partialAuthToken={partialAuthToken}
                fetchUser={fetchUser}
              />
              {error && (
                <>
                  <Alert severity="error" sx={{ mt: 4 }}>
                    {error}
                  </Alert>
                </>
              )}
              {success && (
                <>
                  <Alert severity="success" sx={{ mt: 4 }}>
                    Profile updated successfully
                  </Alert>
                </>
              )}
              {loading && (
                <LinearProgress
                  id="login-loading"
                  color="secondary"
                  sx={{
                    mt: 5,
                    height: "8px",
                    borderRadius: "6px",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#962EFF",
                      borderRadius: "6px",
                    },
                  }}
                />
              )}
            </>
          ) : (
            <p>Loading...</p>
          )}
        </BorderCard>
      </div>
    </ProfileContainer>
  );
};

export default Profile;
